import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiCpu, FiUsers, FiBook, FiShield, FiCoffee, FiGlobe, FiStar, FiSmile, FiMapPin } = FiIcons;

const Personal = ({ viewMode }) => {
  const [activeTab, setActiveTab] = useState('hobbies');

  const hobbies = [
    {
      title: "Martial Arts & Family",
      icon: FiShield,
      description: "Training in BJJ, MMA, and Kickboxing alongside my wife and two sons. It's more than just a workout; it's about building local community bonds and teaching my children the vital life lessons of discipline, resilience, and respect.",
      image: "https://images.unsplash.com/photo-1552072805-2a9039d00e57?w=400&h=300&fit=crop",
      details: ["Active BJJ Practitioner", "Family Gym Sessions", "Community Focused"]
    },
    {
      title: "AI & Automation Lab",
      icon: FiCpu,
      description: "Deeply immersed in the AI ecosystem. I spend my time building SaaS applications, hosting n8n and Supabase instances, and pushing the boundaries of what's possible with AI-enhanced automations.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop",
      details: ["n8n & Supabase Hosting", "SaaS Development", "Workflow Optimization"]
    },
    {
      title: "Men's Mentorship",
      icon: FiUsers,
      description: "Building an online community dedicated to helping young American men mature into adulthood. I focus on bridging the gap between simply finding knowledge and actually attaining the wisdom and will to take action.",
      image: "https://images.unsplash.com/photo-1529070532901-b84406f7b3df?w=400&h=300&fit=crop",
      details: ["Community Building", "Character Development", "Action-Oriented Wisdom"]
    },
    {
      title: "Church Volunteering",
      icon: FiBook,
      description: "Dedicated to serving my local church by teaching 2nd-grade students. It's a privilege to invest in the next generation and provide a solid foundation for their spiritual and personal growth.",
      image: "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?w=400&h=300&fit=crop",
      details: ["2nd Grade Teacher", "Weekly Engagement", "Values-Based Education"]
    }
  ];

  const values = [
    {
      title: "Disciplined Action",
      description: "Whether on the mats or in the server room, discipline is the bridge between goals and accomplishment. I believe in showing up and doing the work.",
      icon: FiShield
    },
    {
      title: "Wisdom Over Knowledge",
      description: "Data is everywhere, but wisdom is rare. I strive to apply technical knowledge in ways that are ethically sound and practically wise.",
      icon: FiStar
    },
    {
      title: "Family Stewardship",
      description: "Leading by example for my wife and sons is my highest calling. Technology is a tool, but family and community are the foundation.",
      icon: FiHeart
    },
    {
      title: "Radical Innovation",
      description: "Specifically in AI and automation—leveraging modern tools to eliminate mundane tasks and free up human potential for higher pursuits.",
      icon: FiCpu
    }
  ];

  const funFacts = [
    "I train BJJ with my whole family—it's our primary bonding activity.",
    "My home lab runs dedicated n8n and Supabase instances for custom AI experiments.",
    "I can explain complex security protocols using 2nd-grade vocabulary (thanks to my students!).",
    "I believe the greatest challenge for young men today isn't a lack of information, but a lack of mentorship.",
    "I've built multiple SaaS apps from scratch just to see how far I could automate the process.",
    "My wife can probably out-kickbox you (and me on a bad day).",
    "I host my own infrastructure to maintain total control over my data and AI workflows.",
    "I'm on a mission to help 1,000 young men find the 'will to take action' this year."
  ];

  const volunteerWork = [
    {
      organization: "Local Church Ministry",
      role: "2nd Grade Teacher",
      description: "Leading weekly lessons, activities, and mentorship for energetic 7 and 8-year-olds.",
      duration: "Ongoing",
      impact: "Building foundational character in 20+ children weekly"
    },
    {
      organization: "Young Men's Online Community",
      role: "Founder & Mentor",
      description: "Creating a digital space for maturation, wisdom-sharing, and accountability for young American men.",
      duration: "Active",
      impact: "Growing a community of action-oriented individuals"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="section-padding bg-gradient-to-r from-teal-700 to-blue-800 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SafeIcon icon={FiHeart} className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
            Off the Clock
          </h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto">
            {viewMode === 'professional' ? 
              "Exploring the intersection of discipline, technology, and community—the driving forces behind the engineer." : 
              "Faith, Family, Martial Arts, and AI. This is what my world looks like when the firewalls are holding steady."}
          </p>
        </motion.div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-6 bg-white border-b">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { id: 'hobbies', label: 'Passions & Projects', icon: FiHeart },
              { id: 'values', label: 'Core Philosophy', icon: FiStar },
              { id: 'fun', label: 'Quick Facts', icon: FiSmile },
              { id: 'volunteer', label: 'Giving Back', icon: FiGlobe }
            ].map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === tab.id ? 'bg-teal-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={tab.icon} className="w-4 h-4 mr-2" />
                {tab.label}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto">
          {activeTab === 'hobbies' && (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {hobbies.map((hobby, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.02, y: -5 }}
                  >
                    <img src={hobby.image} alt={hobby.title} className="w-full h-48 object-cover" />
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <SafeIcon icon={hobby.icon} className="w-6 h-6 text-teal-600 mr-3" />
                        <h3 className="font-heading text-xl font-bold text-gray-900">{hobby.title}</h3>
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">{hobby.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {hobby.details.map((detail, idx) => (
                          <span key={idx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-semibold">
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'values' && (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.02, y: -5 }}
                  >
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={value.icon} className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-4 text-gray-900">{value.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'fun' && (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                {funFacts.map((fact, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-teal-500 hover:shadow-md transition-all"
                  >
                    <p className="text-gray-700 italic">"{fact}"</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'volunteer' && (
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <div className="max-w-4xl mx-auto space-y-8">
                {volunteerWork.map((work, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-white rounded-xl shadow-lg p-8 border border-gray-100"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-2xl font-bold text-gray-900">{work.organization}</h3>
                        <div className="text-lg font-semibold text-teal-600">{work.role}</div>
                      </div>
                      <div className="text-right mt-2 md:mt-0">
                        <div className="text-sm font-bold text-teal-700">{work.duration}</div>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">{work.description}</p>
                    <div className="inline-block px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-bold">
                      Impact: {work.impact}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Philosophy Quote */}
      <section className="section-padding bg-gray-900 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-6 text-teal-400" />
          <blockquote className="text-2xl md:text-3xl font-light italic mb-8 max-w-4xl mx-auto leading-relaxed">
            "Knowledge is common; wisdom is earned. I believe our greatest responsibility is to take action on what we know to build stronger families and more resilient communities."
          </blockquote>
          <p className="text-xl text-gray-400">
            {viewMode === 'professional' ? "The human element of my security philosophy." : "The core of everything I do."}
          </p>
        </motion.div>
      </section>
    </div>
  );
};

export default Personal;